
import React, { useState } from "react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TutorialCreator from "@/components/tutorial-creator/TutorialCreator";
import TutorialList from "@/components/tutorial-creator/TutorialList";
import TutorialViewer from "@/components/tutorial-creator/TutorialViewer";
import { Tutorial } from "@/types/tutorial";
import { useIsMobile } from "@/hooks/use-mobile";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import ErrorBoundary from "@/components/error/ErrorBoundary";

const Tutorials = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { handleError } = useErrorHandler();
  const [currentView, setCurrentView] = useState<"list" | "create" | "view">("list");
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null);
  const [sidebarExpanded,setSidebarExpanded] = useState(!isMobile);

  const handleCreateNew = () => {
    try {
      setCurrentView("create");
      setSelectedTutorial(null);
    } catch (error) {
      handleError(error, {
        context: { action: 'create_tutorial', component: 'Tutorials' }
      });
    }
  };

  const handleViewTutorial = (tutorial: Tutorial) => {
    try {
      if (!tutorial || !tutorial.id) {
        throw new Error('Invalid tutorial data');
      }
      setSelectedTutorial(tutorial);
      setCurrentView("view");
    } catch (error) {
      handleError(error, {
        context: { action: 'view_tutorial', tutorial: tutorial?.id, component: 'Tutorials' }
      });
    }
  };

  const handleBackToList = () => {
    try {
      setCurrentView("list");
      setSelectedTutorial(null);
    } catch (error) {
      handleError(error, {
        context: { action: 'back_to_list', component: 'Tutorials' }
      });
    }
  };

  const toggleSidebar = () => {
    try {
      setSidebarExpanded(!sidebarExpanded);
    } catch (error) {
      handleError(error, {
        context: { action: 'toggle_sidebar', component: 'Tutorials' }
      });
    }
  };

  const renderContent = () => {
    try {
      switch (currentView) {
        case "create":
          return (
            <ErrorBoundary fallback={
              <div className="text-center py-8">
                <p className="text-muted-foreground">Error loading tutorial creator</p>
                <Button onClick={handleBackToList} variant="outline" className="mt-4">
                  Back to Tutorials
                </Button>
              </div>
            }>
              <TutorialCreator 
                onBack={handleBackToList} 
                sidebarExpanded={sidebarExpanded}
                toggleSidebar={toggleSidebar}
              />
            </ErrorBoundary>
          );
        case "view":
          return selectedTutorial ? (
            <ErrorBoundary fallback={
              <div className="text-center py-8">
                <p className="text-muted-foreground">Error loading tutorial</p>
                <Button onClick={handleBackToList} variant="outline" className="mt-4">
                  Back to Tutorials
                </Button>
              </div>
            }>
              <TutorialViewer tutorial={selectedTutorial} onBack={handleBackToList} />
            </ErrorBoundary>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Tutorial not found</p>
              <Button onClick={handleBackToList} variant="outline" className="mt-4">
                Back to Tutorials
              </Button>
            </div>
          );
        default:
          return (
            <ErrorBoundary fallback={
              <div className="text-center py-8">
                <p className="text-muted-foreground">Error loading tutorials list</p>
                <Button onClick={() => window.location.reload()} variant="outline" className="mt-4">
                  Refresh Page
                </Button>
              </div>
            }>
              <TutorialList onViewTutorial={handleViewTutorial} />
            </ErrorBoundary>
          );
      }
    } catch (error) {
      handleError(error, {
        context: { action: 'render_content', currentView, component: 'Tutorials' }
      });
      
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Something went wrong</p>
          <Button onClick={handleBackToList} variant="outline" className="mt-4">
            Back to Tutorials
          </Button>
        </div>
      );
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col bg-background">
        <Header 
          sidebarExpanded={currentView === "create" ? sidebarExpanded : undefined}
          toggleSidebar={currentView === "create" ? toggleSidebar : undefined}
        />
        
        <div className="container py-8 pt-[80px]">
          {currentView === "list" && (
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-3xl font-bold">Tutorials</h1>
                <p className="text-muted-foreground mt-2">
                  Create and follow step-by-step guides for 3D printing modifications
                </p>
              </div>
              {user && (
                <Button onClick={handleCreateNew} className="flex items-center gap-2">
                  <Plus size={16} />
                  Create Tutorial
                </Button>
              )}
            </div>
          )}
          
          {renderContent()}
        </div>
        
        <footer className="bg-card border-t border-border py-6 mt-auto">
          <div className="container">
            <p className="text-sm text-center text-muted-foreground">
              3DP Hub | The ultimate directory for 3D printer parts and upgrades
            </p>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
};

export default Tutorials;
